import { useState } from 'react'
import { BookOpen, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { fetchWithAuth, updateDocument } from '@/lib/api'
import {
  KNOWLEDGE_HUB_STATUSES,
  REVIEW_INTERVAL_OPTIONS,
  REVIEW_STATUS_COLORS,
  canUserMarkKhVerified,
  computeNextReviewDate,
  khEmailListFromFrontmatter,
  reviewIntervalLabel,
  reviewStatusLabel,
  type ReviewStatus,
} from '@/lib/knowledge-hub-utils'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'
import { useAuth } from '@/providers/auth-provider'
import { KnowledgeHubPeoplePicker, useKhWorkspaceMembers } from './knowledge-hub-people-picker'
import { KnowledgeHubRequestReviewModal } from './knowledge-hub-request-review-modal'

export type KnowledgeHubInfoModuleProps = {
  documentId: string
  /** Document workspace. People picker lists this org's members. */
  organizationId?: string
  frontmatter: Record<string, unknown>
  /** Called with merged frontmatter after a successful local save or KH action. */
  onFrontmatterChange: (next: Record<string, unknown>) => void
}

/**
 * Document Info sidebar module for Knowledge Hub-enabled docs.
 * Editable: frontmatter status + review_interval.
 * Lifecycle CTAs (Request review / Mark verified) live on the status strip -
 * status select routes those two values through the same KH APIs so changelog
 * stays in sync; draft / needs-review / archived patch frontmatter only.
 */
export function KnowledgeHubInfoModule({
  documentId,
  organizationId,
  frontmatter,
  onFrontmatterChange,
}: KnowledgeHubInfoModuleProps) {
  const status = String(frontmatter.status || 'draft') as ReviewStatus
  const interval = frontmatter.review_interval
    ? String(frontmatter.review_interval)
    : ''
  const verifiedBy =
    typeof frontmatter.verified_by === 'string' ? frontmatter.verified_by : undefined
  const verifiedAt =
    typeof frontmatter.verified_at === 'string' ? frontmatter.verified_at : undefined
  const { user } = useAuth()
  const approvalRequired = frontmatter.approval_required === true
  const approvers = khEmailListFromFrontmatter(frontmatter, 'approvers')
  const readers = khEmailListFromFrontmatter(frontmatter, 'readers')
  const {
    members,
    loading: membersLoading,
    error: membersError,
  } = useKhWorkspaceMembers(organizationId)
  const self = members.find(
    (member) => member.email.trim().toLowerCase() === (user?.email || '').trim().toLowerCase()
  )
  const canMarkVerified = canUserMarkKhVerified(frontmatter, [user?.email, self?.notify_email])

  const [saving, setSaving] = useState(false)
  const [showRequestModal, setShowRequestModal] = useState(false)
  const colorClass =
    REVIEW_STATUS_COLORS[status] || 'bg-muted text-muted-foreground border-border'

  const nextReview = computeNextReviewDate(verifiedAt, frontmatter.review_interval)
  const nextReviewLabel = nextReview
    ? nextReview.toISOString().slice(0, 10)
    : null

  const notifyDocumentUpdated = () => {
    window.dispatchEvent(
      new CustomEvent('document-updated', { detail: { documentId } })
    )
  }

  const patchFrontmatter = async (patch: Record<string, unknown>, successMsg: string) => {
    setSaving(true)
    const previous = frontmatter
    const next = { ...frontmatter, ...patch }
    onFrontmatterChange(next)
    try {
      await updateDocument(documentId, { frontmatter: next })
      toast.success(successMsg, { duration: 2000, position: 'bottom-right' })
      notifyDocumentUpdated()
    } catch (err) {
      onFrontmatterChange(previous)
      toast.error('Failed to update Knowledge Hub settings', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleStatusChange = async (nextStatus: string) => {
    if (!nextStatus || nextStatus === status || saving) return

    if (nextStatus === 'review-requested') {
      setShowRequestModal(true)
      return
    }

    if (nextStatus === 'active') {
      setSaving(true)
      try {
        const res = await fetchWithAuth('/api/knowledge-hub/mark-verified', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            document_id: documentId,
          }),
        })
        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          throw new Error(
            typeof data.error === 'string' ? data.error : 'Failed to mark verified'
          )
        }
        const data = await res.json().catch(() => ({}))
        const updatedFm =
          (data.document?.frontmatter as Record<string, unknown> | undefined) || {
            ...frontmatter,
            status: 'active',
          }
        onFrontmatterChange(updatedFm)
        toast.success('Marked verified', { duration: 2000, position: 'bottom-right' })
        notifyDocumentUpdated()
      } catch (err) {
        toast.error('Failed to mark verified', {
          description: err instanceof Error ? err.message : 'Unknown error',
        })
      } finally {
        setSaving(false)
      }
      return
    }

    // draft | needs-review | archived - frontmatter only (no KH changelog checkpoint)
    await patchFrontmatter({ status: nextStatus }, 'Status updated')
  }

  const handleIntervalChange = async (nextInterval: string) => {
    if (!nextInterval || nextInterval === interval || saving) return
    await patchFrontmatter(
      {
        review_interval: nextInterval,
        review_enabled: true,
      },
      'Review interval updated'
    )
  }

  const intervalSelectValue =
    interval && REVIEW_INTERVAL_OPTIONS.some((o) => o.value === interval)
      ? interval
      : interval || undefined

  return (
    <>
      <div
        className="rounded-md border border-border bg-muted/30 p-3 space-y-3 min-w-0 max-w-full overflow-hidden"
        data-testid={testIds.knowledgeHub.infoModule}
      >
        <div className="flex items-center gap-2 min-w-0">
          <BookOpen className="h-4 w-4 shrink-0 text-foreground/80" aria-hidden />
          <span className="text-sm font-semibold text-foreground truncate">
            Knowledge Hub
          </span>
          <Badge
            className={cn('border text-xs capitalize shrink-0 ml-auto', colorClass)}
            data-testid={testIds.knowledgeHub.infoStatusChip}
          >
            {reviewStatusLabel(status)}
          </Badge>
          {saving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground shrink-0" />
          ) : null}
        </div>

        <div className="space-y-1.5 min-w-0">
          <Label
            htmlFor="kh-info-status"
            className="text-xs text-muted-foreground font-normal"
          >
            Review status
          </Label>
          <Select
            value={status}
            onValueChange={(v) => void handleStatusChange(v)}
            disabled={saving}
          >
            <SelectTrigger
              id="kh-info-status"
              className="h-8 w-full min-w-0 text-xs"
              data-testid={testIds.knowledgeHub.infoStatusSelect}
            >
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              {KNOWLEDGE_HUB_STATUSES.map((s) => (
                <SelectItem
                  key={s}
                  value={s}
                  className="text-xs capitalize"
                  disabled={s === 'active' && status !== 'active' && !canMarkVerified}
                >
                  {reviewStatusLabel(s)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(verifiedBy || verifiedAt) && (
            <p className="text-xs text-muted-foreground leading-snug">
              {verifiedBy ? `Verified by ${verifiedBy}` : 'Verified'}
              {verifiedAt ? ` (${verifiedAt.slice(0, 10)})` : ''}
            </p>
          )}
        </div>

        <div className="space-y-1.5 pt-1 border-t border-border/60 min-w-0">
          <Label
            htmlFor="kh-info-interval"
            className="text-xs text-muted-foreground font-normal"
          >
            Review interval
          </Label>
          <Select
            value={intervalSelectValue}
            onValueChange={(v) => void handleIntervalChange(v)}
            disabled={saving}
          >
            <SelectTrigger
              id="kh-info-interval"
              className="h-8 w-full min-w-0 text-xs"
              data-testid={testIds.knowledgeHub.infoIntervalSelect}
            >
              <SelectValue placeholder="Select interval" />
            </SelectTrigger>
            <SelectContent>
              {REVIEW_INTERVAL_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} className="text-xs">
                  {o.label}
                </SelectItem>
              ))}
              {interval &&
              !REVIEW_INTERVAL_OPTIONS.some((o) => o.value === interval) ? (
                <SelectItem value={interval} className="text-xs">
                  {reviewIntervalLabel(interval)}
                </SelectItem>
              ) : null}
            </SelectContent>
          </Select>
          {nextReviewLabel ? (
            <p
              className="text-xs text-muted-foreground"
              data-testid={testIds.knowledgeHub.infoNextReview}
            >
              Next review {nextReviewLabel}
            </p>
          ) : null}
        </div>

        <div className="space-y-3 pt-1 border-t border-border/60 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <Label
              htmlFor="kh-info-approval-required"
              className="text-xs text-muted-foreground font-normal"
            >
              Require named approver
            </Label>
            <Switch
              id="kh-info-approval-required"
              checked={approvalRequired}
              disabled={saving}
              onCheckedChange={(checked) =>
                void patchFrontmatter(
                  { approval_required: checked },
                  checked ? 'Approver required enabled' : 'Approver required disabled'
                )
              }
              data-testid={testIds.knowledgeHub.infoApprovalRequiredSwitch}
            />
          </div>
          <KnowledgeHubPeoplePicker
            label="Approvers"
            emails={approvers}
            members={members}
            membersLoading={membersLoading}
            membersError={membersError}
            disabled={saving}
            testId={testIds.knowledgeHub.infoApproversPicker}
            onChange={(next) => void patchFrontmatter({ approvers: next }, 'Approvers updated')}
          />
          <KnowledgeHubPeoplePicker
            label="Readers"
            emails={readers}
            members={members}
            membersLoading={membersLoading}
            membersError={membersError}
            disabled={saving}
            testId={testIds.knowledgeHub.infoReadersPicker}
            onChange={(next) => void patchFrontmatter({ readers: next }, 'Readers updated')}
          />
        </div>
      </div>

      <KnowledgeHubRequestReviewModal
        open={showRequestModal}
        onOpenChange={setShowRequestModal}
        documentId={documentId}
        onSuccess={() => {
          onFrontmatterChange({
            ...frontmatter,
            status: 'review-requested',
          })
          notifyDocumentUpdated()
        }}
      />
    </>
  )
}
